import {HedgeMatrixCellData} from "./hedge-matrix-cell.data";
import {PricingGridStrategyDescriptor} from "../../options-pricing-grid/model/pricing-grid-strategy.descriptor";
import {OptionExpirationDescriptor} from "../../shell-communication/shell-dto-protocol";

// CR-20260901-005 — Undo/Redo for Hedge Matrix quantity edits.
//
// TRANSACTION model (owner design, round 3): one user gesture = one transaction.
// BEGIN silently clones the matrix state; the gesture's mutations run through their normal
// (leaky) paths untouched; END diffs current-vs-BEGIN and stores ONLY the touched cells'
// before/after images. Undo applies the delta's before images, redo its after images —
// cell-scoped, so the apply (and its repaint) is proportional to the gesture, never the grid.
//
// Why diff-at-END instead of logging each mutation: the mutation funnel is leaky (modification
// enter/exit write cell fields directly, rolls delete expiration containers, hedge-level fields
// bypass setQty entirely). A diff of two snapshots tolerates every leak by construction, and
// dedupes multi-writes to the same cell for free. The BEGIN clone is invisible to the UI —
// capture cost was never the flicker; apply breadth was.

/** Per-hedge mutable fields that travel with the cell state. References are kept, not cloned:
 * template/anchorExpiration/modifications are only ever REPLACED wholesale, never edited in place. */
export interface HedgeFieldsSnapshot {
    underModification?: boolean;
    initialExpiration?: string;
    template?: PricingGridStrategyDescriptor;
    anchorExpiration?: OptionExpirationDescriptor;
    modifications?: HedgeMatrixCellData[];
}

export interface HedgeMatrixStateSnapshot {
    cellDataIndex: Record<number, Record<string, Record<string, HedgeMatrixCellData>>>;
    hedgeFields: Record<string, HedgeFieldsSnapshot>;
}

/** One cell's before/after images. `null` = the cell did not exist on that side. */
export interface MatrixCellDelta {
    strike: number;
    hedgeId: string;
    expiration: string;
    before: HedgeMatrixCellData;
    after: HedgeMatrixCellData;
}

export interface MatrixHedgeFieldsDelta {
    hedgeId: string;
    before: HedgeFieldsSnapshot;
    after: HedgeFieldsSnapshot;
}

/** One transaction = one undo step: everything one gesture touched, and nothing else. */
export interface MatrixTransactionDelta {
    cells: MatrixCellDelta[];
    hedgeFields: MatrixHedgeFieldsDelta[];
}

const MAX_UNDO_DEPTH = 50;

// NaN can only be transient in the quantity fields (the setter's coupling pass overwrites it),
// and JSON cloning turns it into null anyway — normalize so the diff never trips on it.
function normQty(v: number): number {
    return (v === null || v === undefined || isNaN(v)) ? null : v;
}

function cellsEqual(a: HedgeMatrixCellData, b: HedgeMatrixCellData): boolean {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return normQty(a.qty) === normQty(b.qty)
        && normQty(a.transQty) === normQty(b.transQty)
        && normQty(a.outcomeQty) === normQty(b.outcomeQty)
        && a.ticker === b.ticker
        && a.actualStrike === b.actualStrike;
}

function hedgeFieldsEqual(a: HedgeFieldsSnapshot, b: HedgeFieldsSnapshot): boolean {
    if (!a && !b) return true;
    if (!a || !b) return false;
    // Primitives by value; template/anchorExpiration/modifications by REFERENCE — they are
    // replaced wholesale on change, never mutated in place.
    return (a.underModification || false) === (b.underModification || false)
        && (a.initialExpiration || null) === (b.initialExpiration || null)
        && a.template === b.template
        && a.anchorExpiration === b.anchorExpiration
        && a.modifications === b.modifications;
}

/** Pure diff of two state snapshots. Both snapshots are discarded by the caller after this,
 * so the delta takes ownership of the image objects — no extra cloning needed. */
export function diffMatrixSnapshots(before: HedgeMatrixStateSnapshot, after: HedgeMatrixStateSnapshot): MatrixTransactionDelta {

    const cells: MatrixCellDelta[] = [];
    const keys = new Set<string>();

    const collectKeys = (index: HedgeMatrixStateSnapshot['cellDataIndex']) => {
        Object.keys(index || {}).forEach(strike => {
            Object.keys(index[strike] || {}).forEach(hedgeId => {
                Object.keys(index[strike][hedgeId] || {}).forEach(expiration => {
                    keys.add(strike + '|' + hedgeId + '|' + expiration);
                });
            });
        });
    };

    collectKeys(before.cellDataIndex);
    collectKeys(after.cellDataIndex);

    keys.forEach(key => {
        const parts = key.split('|');
        const strike = parseInt(parts[0], 10);
        const hedgeId = parts[1];
        const expiration = parts[2];

        const b = before.cellDataIndex[strike]?.[hedgeId]?.[expiration] || null;
        const a = after.cellDataIndex[strike]?.[hedgeId]?.[expiration] || null;

        if (!cellsEqual(b, a)) {
            cells.push({strike, hedgeId, expiration, before: b, after: a});
        }
    });

    const hedgeFields: MatrixHedgeFieldsDelta[] = [];
    const hedgeIds = new Set<string>();
    Object.keys(before.hedgeFields || {}).forEach(id => hedgeIds.add(id));
    Object.keys(after.hedgeFields || {}).forEach(id => hedgeIds.add(id));

    hedgeIds.forEach(hedgeId => {
        const b = before.hedgeFields[hedgeId];
        const a = after.hedgeFields[hedgeId];
        if (!hedgeFieldsEqual(b, a)) {
            hedgeFields.push({hedgeId, before: b, after: a});
        }
    });

    return {cells, hedgeFields};
}

export class HedgeMatrixUndoService {

    private _undoStack: MatrixTransactionDelta[] = [];
    private _redoStack: MatrixTransactionDelta[] = [];
    private _pendingBefore: HedgeMatrixStateSnapshot = null;

    get canUndo(): boolean {
        return this._undoStack.length > 0;
    }

    get canRedo(): boolean {
        return this._redoStack.length > 0;
    }

    /** BEGIN: a gesture is opening — hold its before-state. Replaces any stale pending. */
    beginTransaction(before: HedgeMatrixStateSnapshot): void {
        this._pendingBefore = before;
    }

    get hasPendingTransaction(): boolean {
        return this._pendingBefore !== null;
    }

    /** ROLLBACK-discard: the gesture ended without mutating (cancel / Escape / no-op). */
    abortTransaction(): void {
        this._pendingBefore = null;
    }

    /** END: diff the gesture's after-state against the held before-state. An empty delta
     * (the gesture changed nothing) pushes nothing and leaves the redo stack intact. */
    commitTransaction(after: HedgeMatrixStateSnapshot): void {
        const before = this._pendingBefore;
        this._pendingBefore = null;
        if (!before) {
            return;   // END without BEGIN — e.g. history was cleared mid-gesture
        }
        const delta = diffMatrixSnapshots(before, after);
        if (delta.cells.length === 0 && delta.hedgeFields.length === 0) {
            return;
        }
        this._undoStack.push(delta);
        if (this._undoStack.length > MAX_UNDO_DEPTH) {
            this._undoStack.shift();
        }
        this._redoStack = [];
    }

    /** The delta is bidirectional — the same entry moves between the stacks. */
    undo(): MatrixTransactionDelta {
        if (this._undoStack.length === 0) {
            return null;
        }
        const delta = this._undoStack.pop();
        this._redoStack.push(delta);
        return delta;
    }

    redo(): MatrixTransactionDelta {
        if (this._redoStack.length === 0) {
            return null;
        }
        const delta = this._redoStack.pop();
        this._undoStack.push(delta);
        return delta;
    }

    /** History boundary: portfolio switch/reload or USER hedge add/remove — events that change
     * hedge identity or re-read the book. CoPilot preview transitions are deliberately NOT
     * boundaries (owner ruling, round 4): manual-edit transactions belong to the trader and
     * survive them; the divergence mechanism guards the overlap. */
    clear(): void {
        this._undoStack = [];
        this._redoStack = [];
        this._pendingBefore = null;
    }
}
